/**
 * Navigation helper hook for components
 *
 * Extracted from routes.tsx to avoid circular dependencies.
 * Use this in components to navigate programmatically.
 *
 * Deep linking support:
 * - goToProjectSection(projectId, 'tasks') → /projects/:id/tasks
 * - goToProjectEntity(projectId, 'tasks', taskId) → /projects/:id/tasks/:taskId
 * - closeProjectEntity(projectId, section) → /projects/:id/:section (closes widget)
 */

import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Project section types for deep linking
 */
export type ProjectSection =
  | 'dashboard'
  | 'tasks'
  | 'board'
  | 'tickets'
  | 'milestones'
  | 'files'
  | 'times'
  | 'messages'
  | 'notebooks'
  | 'links'
  | 'comments'
  | 'people'
  | 'gantt'
  | 'timeline'
  | 'table'
  | 'finance'
  | 'risks'
  | 'proofs'
  | 'forms'
  | 'settings';

export function useAppNavigate() {
  const navigate = useNavigate();
  const location = useLocation();

  return {
    // === HOME ===
    goToHome: () => navigate('/home'),

    // === TODAY ===
    goToToday: () => navigate('/today'),
    goToTodayEntity: (entityType: string, entityId: string) =>
      navigate(`/today/${entityType}/${entityId}`),

    // === PROJECTS - Full deep linking ===
    goToProjects: () => navigate('/projects'),
    goToProject: (id: string) => navigate(`/projects/${id}/dashboard`),
    goToProjectEdit: (id: string) => navigate(`/projects/${id}`),

    /**
     * Navigate to a project section
     * @example goToProjectSection('prj-123', 'tasks')
     */
    goToProjectSection: (projectId: string, section: ProjectSection) =>
      navigate(`/projects/${projectId}/${section}`),

    /**
     * Navigate to open an entity widget within a project section
     * @example goToProjectEntity('prj-123', 'tasks', 'tsk-456')
     */
    goToProjectEntity: (projectId: string, section: ProjectSection, entityId: string) =>
      navigate(`/projects/${projectId}/${section}/${entityId}`),

    /**
     * Close entity widget (navigate back to section without entityId)
     * @example closeProjectEntity('prj-123', 'tasks')
     */
    closeProjectEntity: (projectId: string, section: ProjectSection) =>
      navigate(`/projects/${projectId}/${section}`),

    // Legacy aliases for backward compatibility
    goToProjectTasks: (projectId: string) => navigate(`/projects/${projectId}/tasks`),
    goToProjectTask: (projectId: string, taskId: string) =>
      navigate(`/projects/${projectId}/tasks/${taskId}`),

    // === CALENDAR ===
    goToCalendar: () => navigate('/calendar'),
    goToCalendarEvent: (eventId: string) => navigate(`/calendar/events/${eventId}`),

    // === CONTACTS ===
    goToContacts: () => navigate('/contacts'),
    goToContact: (id: string) => navigate(`/contacts/${id}`),

    // === ACCOUNTING ===
    goToAccounting: () => navigate('/accounting'),
    goToInvoice: (id: string) => navigate(`/accounting/invoices/${id}`),
    goToExpenses: () => navigate('/accounting/expenses'),
    goToForecast: () => navigate('/accounting/forecast'),

    // === SETTINGS ===
    goToSettings: (section?: string) => navigate(section ? `/settings/${section}` : '/settings'),

    // === UTILS ===
    /**
     * Get current URL for copying/sharing
     */
    getCurrentUrl: () => window.location.href,

    /**
     * Navigate back
     */
    goBack: () => navigate(-1),

    /**
     * Check if we're on a specific path
     */
    isOnPath: (path: string) => location.pathname.startsWith(path),
  };
}
